/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'
import { PrimordialProviderProps } from '~types/index'

import { ApolloProvider } from '@apollo/client'
import { sendScreenViewEventSSR } from '@mercadoni/sinister/dist/cjs/monitors/hooks' // TODO: review size of this component

// Analytics providers - Defer until after first paint
export const AnalyticsProviders = {
  Monitor: dynamic(() => import('@/components/Monitor'), {
    ssr: false,
    loading: () => null,
  }),
}

import { LocaleProvider } from '@mercadoni/mimic/dist/cjs/locale/contexts/LocaleContext' // TODO: review size of this component
import { ClientModelProvider } from '@mercadoni/sinister/dist/cjs/client/contexts'
import { HeaderDataProvider } from '@mercadoni/sinister/dist/cjs/shared/contexts'

import { setCookieCSR } from '@/app/api/CSR/Cookies'
import { useHeaderDataConfig } from '@/hooks/useHeaderDataConfig'
import { getClient } from '@/lib/ApolloClient/getClient'
import { FALLBACK_CLIENT } from '@/utils/constants'
import isSSR from '@/utils/isSSR'

import useBreakpointCode from '@/hooks/useBreakpointCode/useBreakpointCode'
import { getCurrentScreenName } from '@/hooks/useHeaderDataConfig/getCurrentScreenName'
import useUnHandlerejection from '@/hooks/useUnHandlerejection'
import appLoaded from '@/utils/events/appLoaded'
import getClientLocale from '@/utils/getClientLocale'
import safeSetLocalStorage from '@/utils/safeSetLocalStorage'
import { reportClientSpeedToSentry } from '@/utils/sentrySpeedReporting'
import dynamic from 'next/dynamic'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import AppLoaded from './AppLoaded'

export const PrimordialProvider = ({
  children,
  store,
  clientModel,
}: PrimordialProviderProps) => {
  const {
    clientId = FALLBACK_CLIENT,
    useDomain,
    seoConfig,
    ga4Id = '',
    monitor,
  } = clientModel || {}

  const isUseDomain = useDomain || false

  // Memoize defaultSeoConfig to prevent recreation on every render
  const defaultSeoConfig = useMemo(() => ({ defaultTitle: '' }), [])

  const router = useRouter()

  // Memoize Apollo client to prevent recreation on every render
  const apolloClient = useMemo(
    () => getClient({ onError: console.error, router }),
    [router]
  )

  const { version, screenNames, loading } = useHeaderDataConfig(clientModel)

  useUnHandlerejection()

  useEffect(() => {
    reportClientSpeedToSentry({
      includeDeviceInfo: true,
    })
  }, [])

  const pathname = usePathname()

  useEffect(() => {
    if (!isSSR()) {
      setCookieCSR('_IL-enabled-v3', '1')
      safeSetLocalStorage('client', clientModel)
      safeSetLocalStorage('selectedStore', store)
    }
  }, [clientId, ga4Id, apolloClient, version, monitor, clientModel, store])

  const currentScreenName = useMemo(() => {
    if (loading) return null
    return getCurrentScreenName({
      screenNames,
      pathname,
      useDomain: isUseDomain,
      seoConfig: seoConfig || defaultSeoConfig,
    })
  }, [loading, pathname])

  useEffect(() => {
    if (loading || !currentScreenName) {
      return
    }

    const { screenName }: any = currentScreenName

    if (pathname !== '/') {
      appLoaded.execute()
      if (screenName) {
        sendScreenViewEventSSR({ clientId, store, version, screenName })
      }
    }
  }, [pathname, loading, currentScreenName, store])

  const clientLocale = useMemo(
    () => getClientLocale(clientModel),
    [clientModel]
  )

  useBreakpointCode({
    customBreakpoints: clientModel?.dynamicHome?.breakpoints,
  })

  if (!clientId) {
    return <>{children}</>
  }

  return (
    <ApolloProvider client={apolloClient}>
      <ClientModelProvider clientModel={clientModel}>
        <HeaderDataProvider
          screenNames={screenNames}
          loading={loading}
          version={version}
          ssr={false}
        >
          <AppLoaded clientConfig={clientModel}>
            <LocaleProvider
              clientId={clientModel?.clientId}
              language={clientLocale}
              ssr={false}
            >
              {children}
              <AnalyticsProviders.Monitor
                clientId={clientId}
                ga4Id={ga4Id}
                apolloClient={apolloClient}
                version={version}
                monitor={monitor}
              />
            </LocaleProvider>
          </AppLoaded>
        </HeaderDataProvider>
      </ClientModelProvider>
    </ApolloProvider>
  )
}
